import moment from 'moment'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'

import BookingItemCard from '../ItemCard/BookingItemCard'

import { fetchCruiseDepartureById } from 'actions/CruiseActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import GeoContext from 'contexts/geoContext'
import { useCruise } from 'hooks/apiQuery/cruise'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formatDynamicAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import { OfferTypeForTripItemTypes } from 'tripPlanner/hooks/api/common'
import { concatStringWithDot } from 'tripPlanner/utils'

interface MappedProps extends Props {
  departures?: { [key: string]: App.CruiseDepartureView }
}

interface Props {
  order: App.Order
  orderItem: App.CruiseOrderItem
  showBorder?: boolean
  onSelect?: (
    order: App.Order,
    offers: Array<OfferTypeForTripItemTypes>,
    checked: boolean,
  ) => void
}

function CruisesBookingCard(props: Props & MappedProps) {
  const { departures, order, orderItem, showBorder, onSelect } = props

  const propertyName = order.cruiseItems[0].offerName ?? ''

  const offerId = departures?.[orderItem.departureId]?.offer.id ?? ''

  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchCruiseDepartureById(orderItem.departureId))
  }, [dispatch, orderItem.departureId])

  const { cruise: offer } = useCruise(offerId)

  const reservationDates = useMemo(() => {
    let dateString = ''
    if (orderItem.departureDate) {
      dateString += moment(orderItem.departureDate).format('DD MMM')
    }

    if (orderItem.arrivalDate) {
      dateString += ' - ' + moment(orderItem.arrivalDate).format('DD MMM YYYY')
    }

    return dateString
  }, [orderItem.arrivalDate, orderItem.departureDate])

  const subHeading = useMemo(
    () =>
      concatStringWithDot(
        reservationDates,
        formatDynamicAmountWithDollarType(
          orderItem.total,
          currentCurrency,
          currentRegionCode,
        ),
      ),
    [currentCurrency, currentRegionCode, reservationDates, orderItem.total],
  )

  const orderWithOfferId = useMemo(() => {
    return {
      ...order,
      cruiseItems: [
        {
          ...order.cruiseItems[0],
          cruiseOfferId: offerId,
        },
        ...order.cruiseItems.slice(1),
      ],
    }
  }, [order, offerId])

  const handleSelect = useCallback(
    (checked: boolean) => {
      if (onSelect && offer) {
        onSelect(orderWithOfferId, [offer], checked)
      }
    },
    [onSelect, orderWithOfferId, offer],
  )

  if (!offer) {
    return <LoadingIndicator inline />
  }

  return (
    <BookingItemCard
      heading={propertyName}
      image={offer.images[0]}
      subHeading={subHeading}
      showBorder={showBorder}
      onSelect={handleSelect}
    />
  )
}

function mapStateToProps(appState: App.State) {
  return {
    departures: appState.cruise.cruiseDeparture,
  }
}
export default connect(mapStateToProps)(CruisesBookingCard)
